import { Box, Button, FormHelperText, Typography, useTheme } from "@mui/material";
import CircularProgress from '@mui/material/CircularProgress';
import { Form, Formik } from 'formik';
import React, { useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import * as yup from 'yup';
import { FormikRoundedCheckBox } from "../../Common/components/Formik/FormikRoundedCheckBox";
import FormikTextInput from "../../Common/components/Formik/FormikTextInput";
import log from "../../Common/services/LogService";
import { CUSTOMER_INFORMATION_MODAL_BUTTONS_PADDING_Y, CUSTOMER_INFORMATION_MODAL_TEXTINPUT_PADDING_X } from "../../customers/configs/CustomerInformationModalConfig";
import { CustomerInformationModalFormToDisplay } from "../../customers/models/CustomerInformationModalFormToDisplay";
import { CustomerInformationModalActions } from "../../customers/redux/CustomerInformationModalActions";
import { SignInProviders } from "../../my-lemonade-library/src/authentications/models/BaseUser";
import SigninConfirmatinMode from "../../my-lemonade-library/src/authentications/models/SigninConfirmationMode";
import { RootState, useTypedSelector } from "../../redux/root-reducer";
import { getAuthenticationProviderButtonColor } from "../helpers/AuthenticationHelpers";
import AuthenticationErrorFormParameters from "../models/AuthenticationErrorFormParameters";
import AuthenticationPinConfirmationFormParameters from "../models/AuthenticationPinConfirmationFormParameters";
import AuthenticationActions from "../redux/AuthenticationActions";
import { signinWithEmail } from "../services/AuthenticationService";
import TermsAndConditionsTextWithModal from "./TermsAndConditionsTextWithModal";

// TODO: review the styles, maybe convert the form to a simpler one as done in ServicePickupInfo.tsx

export const getRestoButtonIconUrl = (): string => {
    return `${process.env.PUBLIC_URL}/assets/providers-icons/getresto.svg`;
}

export interface SigninGetrestoModel {
    email: string;
    accept_terms: boolean;
}

interface SigninWithGetRestoProps {
    onSignInSuccess?: (userCreated?: boolean) => void;
    buttonTitleId?: string;
}

/**
 * Signin with getresto component
 * Must be used only in an authentication modal
 */
const SigninWithGetResto: React.FC<SigninWithGetRestoProps> = (props) => {

    const {
        onSignInSuccess,
        buttonTitleId
    } = props

    const intl = useIntl();
    const dispatch = useDispatch();

    const [error, setError] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const { tableLinkId, themeName } = useTypedSelector((state: RootState) => state.locations);
    const { formToDisplay, formParameters } = useTypedSelector((state: RootState) => state.customerInformationModal);
    const { data } = useTypedSelector((state: RootState) => state.authentication)
    const { order } = useTypedSelector((state: RootState) => state.order);
    const theme = useTheme();

    const initialValues: SigninGetrestoModel = {
        email: (order.customer && order.customer.email) ? order.customer.email : "",
        accept_terms: false,
    }

    const onWantedSignIn = async (signInModel: SigninGetrestoModel) => {

        if (
            !data.user_authentication_state.is_anonymous
            || (
                data.user_authentication_state.user?.sign_in_provider
                && data.user_authentication_state.user?.sign_in_provider !== SignInProviders.ANONYMOUS
            )
        ) {
            log.error(`Wanted to login with Getresto but user ${data.user_authentication_state.user?.uid} is already logged in. Closing the modal.`, {
                user_authentication_state: data.user_authentication_state,
            });
            dispatch(CustomerInformationModalActions.closeCustomerInformationModalIfDisplayed(CustomerInformationModalFormToDisplay.AUTHENTICATION_ADVANTAGES));
            return;
        }

        const { email } = signInModel;
        const signinProvider = SignInProviders.GETRESTO;
        try {
            const response = await signinWithEmail(signinProvider, email, tableLinkId, themeName, order.id);
            if (response.need_confirmation === SigninConfirmatinMode.PIN_OR_EMAIL) {
                const authenticationPinConfirmationFormParameters: AuthenticationPinConfirmationFormParameters = {
                    email: email,
                    provider: signinProvider,
                    uuid: response.user_ref,
                    state: response.state,
                    previousForm: formToDisplay ?? undefined,
                    previousFormParameters: formParameters,
                }
                dispatch(CustomerInformationModalActions.setCustomerInformationModal(
                    CustomerInformationModalFormToDisplay.AUTHENTICATION_PIN_CONFIRMATION,
                    authenticationPinConfirmationFormParameters
                ));
            } else if (response.custom_token && response.customer) {

                if (onSignInSuccess) {
                    onSignInSuccess(true);
                }

                dispatch(AuthenticationActions.signInWithCustomToken(signinProvider, response.state, response.custom_token, response.customer, undefined, CustomerInformationModalFormToDisplay.AUTHENTICATION_CONFIRMATION));
            }
        } catch (error) {
            log.error("Error signin with GetResto", error);
            setError(true);
            setErrorMessage("authentication.authenticationError.error_getresto");
        }
    }

    const validationSchema = yup.object().shape({
        email: yup
            .string()
            .max(70, "authentication.signin.email.validation.maxString70")
            .email("authentication.signin.email.validation.emailFormat")
            .required("authentication.signin.email.validation.emailRequired")
            .test('domains', 'validation', function (value) {
                if (data.location_authentication_config.email_domains_allowed && data.location_authentication_config.email_domains_allowed.length && value) {
                    const domain = data.location_authentication_config.email_domains_allowed.find(domain => value?.includes(domain))
                    if (domain) {
                        return true
                    } else {
                        const formParameters: AuthenticationErrorFormParameters = {
                            errorMessageId: 'authentication.authentication_error.invalid_email_domain',
                        };
                        dispatch(CustomerInformationModalActions.setCustomerInformationModal(
                            CustomerInformationModalFormToDisplay.AUTHENTICATION_ERROR,
                            formParameters,
                        ));
                        return false
                    }
                } else {
                    return true
                }
            }),
        accept_terms: yup
            .bool()
            .required(intl.formatMessage({ id: 'authentication.signup.phone.validation.accept_terms' }))
            .oneOf([true], intl.formatMessage({ id: 'authentication.signup.phone.validation.accept_terms' })),
    });



    return (
        <Box
            width="100%"
            display='flex'
            flexDirection='column'
            data-test="customer.modal.signin"
        >

            <Box
                display='flex'
                paddingTop={3}
                flexDirection='column'
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                {error && errorMessage &&

                    <FormHelperText
                        style={{
                            alignSelf: 'flex-start',
                            marginBottom: theme.spacing(2),
                        }}
                        error
                    >

                        <Typography>
                            {intl.formatMessage({ id: errorMessage })}
                        </Typography>

                    </FormHelperText>
                }

                <Formik<SigninGetrestoModel>
                    validateOnBlur={true}
                    validateOnChange={false}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={async (data, { setErrors, setSubmitting }) => {
                        setSubmitting(true)
                        await onWantedSignIn(data)
                        setSubmitting(false)
                    }}
                >

                    {
                        ({ isSubmitting }) => (

                            <Form
                                style={{ display: 'flex', flexDirection: 'column', width: "100%" }}
                                autoComplete="off"
                            >
                                <Box display="flex" flexDirection="column" width={1}
                                    alignItems="center"
                                    paddingX={CUSTOMER_INFORMATION_MODAL_TEXTINPUT_PADDING_X}>
                                    <FormikTextInput
                                        inputProps={{
                                            "data-test": "customer-modal.signin.email"
                                        }}
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        color="primary"
                                        placeholder={intl.formatMessage({ id: "auth.email.inputplaceholder" })}
                                        fullWidth
                                        style={{
                                            marginBottom: theme.spacing(1.5),
                                        }}
                                    />
                                </Box>

                                <Box width="100%" mt={0.3} display="flex" alignItems="center">

                                    <FormikRoundedCheckBox
                                        inputProps={{
                                            "data-test": "signup_form.accept_terms"
                                        }}

                                        style={{
                                            paddingLeft: 0,
                                        }}
                                        color="primary"
                                        name="accept_terms"
                                    />

                                    <TermsAndConditionsTextWithModal />

                                </Box>

                                <Box mt={1.5} display="flex" justifyContent="center">

                                    <Button
                                        data-test='signin_button'
                                        color="primary"
                                        variant="contained"
                                        style={{
                                            width: "100%",
                                            backgroundColor: getAuthenticationProviderButtonColor(SignInProviders.GETRESTO),
                                            textTransform: "none",
                                            padding: theme.spacing(CUSTOMER_INFORMATION_MODAL_BUTTONS_PADDING_Y, 0),
                                        }}
                                        type="submit"
                                    >
                                        {
                                            isSubmitting &&
                                            (
                                                <CircularProgress
                                                    data-test='signin_loader'
                                                    disableShrink
                                                    size="1rem"
                                                    style={{
                                                        color: theme.palette.primary.contrastText,
                                                        marginRight: theme.spacing(1)
                                                    }}
                                                />
                                            )
                                        }
                                        <img
                                            src={getRestoButtonIconUrl()}
                                            alt=""
                                            style={{
                                                maxHeight: "60%",
                                                marginRight: theme.spacing(1),
                                            }}
                                        />
                                        <Typography variant="subtitle1">
                                            {intl.formatMessage({ id: buttonTitleId ? buttonTitleId : "auth.my_account.login" })}
                                        </Typography>
                                    </Button>

                                </Box>
                            </Form>
                        )
                    }

                </Formik>

            </Box>
        </Box >
    );
}

export default SigninWithGetResto;
